import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Trophy, Timer, User, Users, NotebookPen, ChevronDown, BookOpen } from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import GameMenu from "@/games/shared/GameMenu";
import PlayerSetup from "@/games/shared/PlayerSetup";
import Scoreboard from "@/games/shared/Scoreboard";
import TurnTimer from "@/games/shared/TurnTimer";
import MatchResultDialog from "@/games/shared/MatchResultDialog";
import { useHotSeatMatch, type PlayerId } from "@/games/shared/useHotSeatMatch";
import { play } from "@/games/shared/sound";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  DIFFICULTY_LABEL,
  FAST_THRESHOLD_S,
  POINTS_FAST,
  POINTS_SLOW,
  QUIZ_TURN_SECONDS,
  generateQuestion,
  scoreAnswer,
  type QuizDifficulty,
  type QuizQuestion,
} from "@/games/speedQuiz/engine";

const QUESTIONS_PER_MATCH = 10;
const RESULT_PAUSE_MS = 1700;

type QuizMode = "solo" | "hot-seat";

type SetupState = {
  mode: QuizMode | null;
  difficulty: QuizDifficulty;
};

type SoloPhase =
  | { kind: "setup" }
  | {
      kind: "playing";
      questionIndex: number; // 0-based
      question: QuizQuestion;
      startedAt: number;
      score: number;
    }
  | {
      kind: "result";
      questionIndex: number;
      question: QuizQuestion;
      chosenIndex: number | null;
      score: number;
      lastAward: number;
      outcome: "fast" | "slow" | "wrong" | "timeout";
    }
  | { kind: "finished"; score: number; best: number };

type HotSeatPhase =
  | { kind: "answering"; question: QuizQuestion; startedAt: number }
  | {
      kind: "result";
      question: QuizQuestion;
      chosenIndex: number | null;
      lastAward: number;
      outcome: "fast" | "slow" | "wrong" | "timeout";
    }
  | { kind: "handoff"; nextPlayer: PlayerId };

const SOLO_BEST_KEY = "elementpad:speed-quiz:best";

const readBest = (difficulty: QuizDifficulty): number => {
  try {
    const raw = window.localStorage.getItem(`${SOLO_BEST_KEY}:${difficulty}`);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
};
const writeBest = (difficulty: QuizDifficulty, score: number) => {
  try {
    window.localStorage.setItem(`${SOLO_BEST_KEY}:${difficulty}`, String(score));
  } catch {
    /* ignore */
  }
};

/**
 * Periodic Table Speed Quiz.
 *
 * Two modes:
 *  - Solo: one player answers 10 questions, racing the clock for points.
 *  - Hot-seat 2-player: players alternate questions; highest total wins.
 *
 * Scoring (both modes):
 *  - Correct in ≤4s : 15 pts
 *  - Correct (slow) : 8 pts
 *  - Wrong / timeout: 0 pts
 */
export default function SpeedQuiz() {
  const navigate = useNavigate();
  const [setup, setSetup] = useState<SetupState>({ mode: null, difficulty: "normal" });

  if (!setup.mode) {
    return <ModeSelect setup={setup} setSetup={setSetup} />;
  }
  if (setup.mode === "solo") {
    return (
      <SoloMatch
        difficulty={setup.difficulty}
        onChangeMode={() => setSetup({ mode: null, difficulty: setup.difficulty })}
        onExit={() => navigate("/play")}
      />
    );
  }
  return (
    <HotSeatMatch
      difficulty={setup.difficulty}
      onChangeMode={() => setSetup({ mode: null, difficulty: setup.difficulty })}
      onExit={() => navigate("/play")}
    />
  );
}

// ---------------- mode select ----------------

function ModeSelect({
  setup,
  setSetup,
}: {
  setup: SetupState;
  setSetup: (s: SetupState) => void;
}) {
  return (
    <GameLayout
      title="Periodic Table Speed Quiz"
      subtitle="Race the clock to identify elements from clues. 10 questions per round."
      headerActions={<GameMenu gameKey="speed-quiz" />}
    >
      <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <NotebookPen size={16} className="text-primary" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Choose a mode</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Solo for high scores, or hot-seat to compete head-to-head on one device.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
          <button
            type="button"
            onClick={() => setSetup({ ...setup, mode: "solo" })}
            className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-2 mb-1">
              <User size={14} className="text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">Solo</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              One player. Best score saved per difficulty.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setSetup({ ...setup, mode: "hot-seat" })}
            className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-2 mb-1">
              <Users size={14} className="text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">Hot-seat 2P</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Alternate questions. Highest score wins.
            </p>
          </button>
        </div>

        <div>
          <Label htmlFor="difficulty" className="block text-xs font-medium text-foreground mb-2">
            Difficulty
          </Label>
          <select
            id="difficulty"
            value={setup.difficulty}
            onChange={(e) => setSetup({ ...setup, difficulty: e.target.value as QuizDifficulty })}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="easy">{DIFFICULTY_LABEL.easy}</option>
            <option value="normal">{DIFFICULTY_LABEL.normal}</option>
            <option value="hard">{DIFFICULTY_LABEL.hard}</option>
          </select>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            {QUIZ_TURN_SECONDS}s per question · ≤{FAST_THRESHOLD_S}s = {POINTS_FAST} pts · slower correct = {POINTS_SLOW} pts.
          </p>
        </div>
      </div>
    </GameLayout>
  );
}

// ---------------- solo ----------------

function SoloMatch({
  difficulty,
  onChangeMode,
  onExit,
}: {
  difficulty: QuizDifficulty;
  onChangeMode: () => void;
  onExit: () => void;
}) {
  const [phase, setPhase] = useState<SoloPhase>(() => makeFirstQuestion(difficulty, 0));
  const askedRef = useRef<Set<string>>(new Set());
  const advanceTimer = useRef<number | null>(null);

  // Track asked ids to avoid repeats.
  useEffect(() => {
    if (phase.kind === "playing") askedRef.current.add(phase.question.id);
  }, [phase]);

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const handleAnswer = useCallback(
    (chosenIndex: number | null) => {
      if (phase.kind !== "playing") return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreAnswer(chosenIndex, phase.question, elapsed);
      const nextScore = phase.score + points;
      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");

      setPhase({
        kind: "result",
        questionIndex: phase.questionIndex,
        question: phase.question,
        chosenIndex,
        score: nextScore,
        lastAward: points,
        outcome,
      });

      advanceTimer.current = window.setTimeout(() => {
        const nextIndex = phase.questionIndex + 1;
        if (nextIndex >= QUESTIONS_PER_MATCH) {
          const best = readBest(difficulty);
          if (nextScore > best) writeBest(difficulty, nextScore);
          play(nextScore > best ? "win" : "click");
          setPhase({ kind: "finished", score: nextScore, best: Math.max(best, nextScore) });
          return;
        }
        const nextQ = generateQuestion(Math.random, difficulty, askedRef.current);
        askedRef.current.add(nextQ.id);
        setPhase({
          kind: "playing",
          questionIndex: nextIndex,
          question: nextQ,
          startedAt: performance.now(),
          score: nextScore,
        });
      }, RESULT_PAUSE_MS);
    },
    [phase, difficulty],
  );

  const onExpire = useCallback(() => handleAnswer(null), [handleAnswer]);

  const restart = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    setPhase(makeFirstQuestion(difficulty, 0));
  };

  if (phase.kind === "finished") {
    return (
      <GameLayout
        title="Periodic Table Speed Quiz"
        subtitle={`Solo · ${DIFFICULTY_LABEL[difficulty]}`}
        headerActions={<GameMenu gameKey="speed-quiz" onReset={restart} resetLabel="Restart" />}
      >
        <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6 text-center">
          <Trophy size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Final score</p>
          <p className="text-4xl font-bold font-mono text-foreground tabular-nums mb-1">
            {phase.score}
          </p>
          <p className="text-xs text-muted-foreground mb-5">
            Personal best: <span className="font-mono">{phase.best}</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button variant="outline" onClick={onExit}>Quit</Button>
            <Button variant="outline" onClick={onChangeMode}>Change mode</Button>
            <Button onClick={restart}>Play again</Button>
          </div>
        </div>
      </GameLayout>
    );
  }

  // playing or result
  const active = phase.kind === "playing" ? phase : null;
  const result = phase.kind === "result" ? phase : null;
  const q = active?.question ?? result!.question;
  const idx = active?.questionIndex ?? result!.questionIndex;
  const score = active?.score ?? result!.score;

  return (
    <GameLayout
      title="Periodic Table Speed Quiz"
      subtitle={`Solo · ${DIFFICULTY_LABEL[difficulty]}`}
      headerActions={<GameMenu gameKey="speed-quiz" onReset={restart} resetLabel="Restart" />}
    >
      {/* HUD */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Question" value={`${idx + 1} / ${QUESTIONS_PER_MATCH}`} />
        <Stat label="Score" value={String(score)} mono />
        <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Time</span>
          {active ? (
            <TurnTimer
              key={`solo-${q.id}`}
              seconds={QUIZ_TURN_SECONDS}
              onExpire={onExpire}
              size={40}
            />
          ) : (
            <Timer size={18} className="text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </div>

      <QuestionCard
        question={q}
        chosenIndex={result?.chosenIndex ?? null}
        onChoose={(i) => active && handleAnswer(i)}
        revealed={!!result}
      />

      {result && (
        <ResultBanner
          outcome={result.outcome}
          award={result.lastAward}
          explanation={q.explanation}
          whyCorrect={q.whyCorrect}
        />
      )}
    </GameLayout>
  );
}

function makeFirstQuestion(difficulty: QuizDifficulty, score: number): SoloPhase {
  const q = generateQuestion(Math.random, difficulty, new Set());
  return {
    kind: "playing",
    questionIndex: 0,
    question: q,
    startedAt: performance.now(),
    score,
  };
}

// ---------------- hot-seat ----------------

function HotSeatMatch({
  difficulty,
  onChangeMode,
  onExit,
}: {
  difficulty: QuizDifficulty;
  onChangeMode: () => void;
  onExit: () => void;
}) {
  const match = useHotSeatMatch(0);
  const [phase, setPhase] = useState<HotSeatPhase | null>(null);
  const askedRef = useRef<Set<string>>(new Set());
  const advanceTimer = useRef<number | null>(null);
  const TOTAL_TURNS = QUESTIONS_PER_MATCH * 2; // each player gets 10 questions

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const startMatch = useCallback(
    (p1: string, p2: string) => {
      askedRef.current = new Set();
      match.start(p1, p2);
      setPhase({ kind: "handoff", nextPlayer: 0 });
    },
    [match],
  );

  const beginTurn = useCallback(() => {
    const q = generateQuestion(Math.random, difficulty, askedRef.current);
    askedRef.current.add(q.id);
    setPhase({ kind: "answering", question: q, startedAt: performance.now() });
  }, [difficulty]);

  const submitAnswer = useCallback(
    (chosenIndex: number | null) => {
      if (!phase || phase.kind !== "answering") return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreAnswer(chosenIndex, phase.question, elapsed);

      const current = match.state.currentTurn;
      const newScore = match.state.stats[current] + points;
      match.setStat(current, newScore);

      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");

      setPhase({
        kind: "result",
        question: phase.question,
        chosenIndex,
        lastAward: points,
        outcome,
      });

      advanceTimer.current = window.setTimeout(() => {
        // Match end?
        if (match.state.turnNumber >= TOTAL_TURNS) {
          const a = match.state.stats[0] + (current === 0 ? points : 0);
          const b = match.state.stats[1] + (current === 1 ? points : 0);
          const winner: PlayerId | null = a === b ? null : a > b ? 0 : 1;
          play(winner === null ? "click" : "win");
          match.finish(winner);
          return;
        }
        match.nextTurn();
        const next: PlayerId = current === 0 ? 1 : 0;
        setPhase({ kind: "handoff", nextPlayer: next });
      }, RESULT_PAUSE_MS);
    },
    [phase, match, TOTAL_TURNS],
  );

  const onExpire = useCallback(() => submitAnswer(null), [submitAnswer]);

  const handleRematch = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    match.start(match.state.players[0].name, match.state.players[1].name);
    setPhase({ kind: "handoff", nextPlayer: 0 });
  };
  const handleChangePlayers = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    match.reset();
    setPhase(null);
  };

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Periodic Table Speed Quiz"
        subtitle={`Hot-seat · ${DIFFICULTY_LABEL[difficulty]} · ${QUESTIONS_PER_MATCH} questions each`}
        headerActions={<GameMenu gameKey="speed-quiz" />}
      >
        <PlayerSetup
          title="Set up the match"
          description="Two players alternate questions on the same device. Highest total wins."
          ctaLabel="Start quiz"
          onStart={startMatch}
        />
        <div className="max-w-md mx-auto mt-3 text-center">
          <button
            type="button"
            onClick={onChangeMode}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Change mode
          </button>
        </div>
      </GameLayout>
    );
  }

  const matchOver = match.state.status === "finished";
  const current = match.state.players[match.state.currentTurn];
  const turnsRemaining = Math.max(0, TOTAL_TURNS - match.state.turnNumber + 1);

  return (
    <GameLayout
      title="Periodic Table Speed Quiz"
      subtitle={`Hot-seat · ${DIFFICULTY_LABEL[difficulty]}`}
      headerActions={<GameMenu gameKey="speed-quiz" onReset={handleRematch} resetLabel="Restart" />}
    >
      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="Score" />
      </div>

      {phase?.kind === "handoff" && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">Pass the device to:</p>
          <p className="text-2xl font-bold text-foreground mb-1">{current.name}</p>
          <p className="text-[11px] text-muted-foreground mb-4">
            Turn {match.state.turnNumber} of {TOTAL_TURNS} · {turnsRemaining} left
          </p>
          <Button onClick={beginTurn} size="lg">
            <NotebookPen size={14} className="mr-1.5" aria-hidden="true" />
            I'm ready
          </Button>
        </div>
      )}

      {phase?.kind === "answering" && !matchOver && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-muted-foreground">{current.name}'s turn</span>
            <TurnTimer
              key={`hs-${phase.question.id}`}
              seconds={QUIZ_TURN_SECONDS}
              onExpire={onExpire}
              size={44}
            />
          </div>
          <QuestionCard
            question={phase.question}
            chosenIndex={null}
            onChoose={submitAnswer}
            revealed={false}
          />
        </>
      )}

      {phase?.kind === "result" && !matchOver && (
        <>
          <QuestionCard
            question={phase.question}
            chosenIndex={phase.chosenIndex}
            onChoose={() => undefined}
            revealed
          />
          <ResultBanner
            outcome={phase.outcome}
            award={phase.lastAward}
            explanation={phase.question.explanation}
            whyCorrect={phase.question.whyCorrect}
          />
        </>
      )}

      <MatchResultDialog
        state={match.state}
        open={matchOver}
        onRematch={handleRematch}
        onChangePlayers={handleChangePlayers}
        onExit={onExit}
        detail={`${QUESTIONS_PER_MATCH} questions each. Higher total wins.`}
      />
    </GameLayout>
  );
}

// ---------------- shared sub-components ----------------

function Stat({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
        {label}
      </div>
      <div
        className={`text-base font-semibold text-foreground ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function QuestionCard({
  question,
  chosenIndex,
  onChoose,
  revealed,
}: {
  question: QuizQuestion;
  chosenIndex: number | null;
  onChoose: (i: number) => void;
  revealed: boolean;
}) {
  return (
    <div className="rounded-xl border border-primary/40 bg-card p-5 mb-3">
      <h2 className="text-base font-semibold text-foreground mb-4 leading-snug">
        {question.prompt}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="group" aria-label="Answer choices">
        {question.choices.map((choice, i) => {
          const isCorrect = i === question.correctIndex;
          const isChosen = i === chosenIndex;
          const stateClasses = revealed
            ? isCorrect
              ? "border-primary bg-primary/10 text-foreground"
              : isChosen
                ? "border-destructive/60 bg-destructive/10 text-foreground"
                : "border-border bg-background text-muted-foreground"
            : "border-border bg-background hover:border-primary/60 hover:bg-accent/40";
          return (
            <button
              key={i}
              type="button"
              onClick={() => !revealed && onChoose(i)}
              disabled={revealed}
              className={`text-left p-3 rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${stateClasses} disabled:cursor-default`}
            >
              <span className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded text-[10px] font-bold bg-muted text-muted-foreground">
                {String.fromCharCode(65 + i)}
              </span>
              <span className="text-sm">{choice}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultBanner({
  outcome,
  award,
  explanation,
  whyCorrect,
}: {
  outcome: "fast" | "slow" | "wrong" | "timeout";
  award: number;
  explanation: string;
  whyCorrect: string;
}) {
  const positive = outcome === "fast" || outcome === "slow";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-md border p-3 ${
        positive
          ? "border-primary/40 bg-primary/5"
          : "border-destructive/30 bg-destructive/5"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {positive ? (
          <CheckCircle2 size={14} className="text-primary" aria-hidden="true" />
        ) : (
          <XCircle size={14} className="text-destructive" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {outcome === "fast" && `Fast & correct! +${award} pts`}
          {outcome === "slow" && `Correct. +${award} pts`}
          {outcome === "wrong" && `Wrong - no points`}
          {outcome === "timeout" && `Time's up - no points`}
        </span>
        {positive && (
          <Badge variant="secondary" className="text-[10px] ml-auto">
            {outcome === "fast" ? "Speed bonus" : "Standard"}
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{explanation}</p>
      <WhyCorrect text={whyCorrect} defaultOpen={!positive} />
    </div>
  );
}

function WhyCorrect({ text, defaultOpen = false }: { text: string; defaultOpen?: boolean }) {
  return (
    <details
      className="mt-2 group rounded-md border border-border/60 bg-background/40 open:bg-background/60"
      open={defaultOpen}
    >
      <summary className="cursor-pointer list-none px-2.5 py-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
        <BookOpen size={12} className="text-primary" aria-hidden="true" />
        <span>Why this is correct</span>
        <ChevronDown
          size={12}
          className="ml-auto text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <p className="px-2.5 pb-2.5 pt-0.5 text-xs text-muted-foreground leading-relaxed">
        {text}
      </p>
    </details>
  );
}
